import Lightbox from "lightbox-react";
import { useMemo, useState } from "react";
import Tooltip from "react-tooltip-lite";
import {
  OrderItemStatus,
  OrderItem as OrderItemType,
} from "../../Generated/graphql";
import { getImageWithSize } from "../../Utils/getImageWithSize";
import { Checkbox } from "../Checkbox/Checkbox";
import { Icon } from "../Icon/Icon";
import { SplitSelect } from "../SplitSelect/SplitSelect";
import "./OrderItem.css";
import { OrderItemContainer } from "./OrderItemContainer";

interface IOrderItemProps {
  item: OrderItemType;
  windowWidth: number;
  isSelected: boolean;
  isLoading: boolean;
  displayStatus: OrderItemStatus;
  displayExcluded: boolean;
  hasZeroAmount?: boolean;
  isLast?: boolean;
  onAddToInventory: (included: boolean) => void;
  onRemoveItem: () => void;
  onSelectItem: () => void;
  onChangeAmount: (amount: number) => void;
  onChangeExcluded: (excluded: boolean) => void;
}

export const OrderItem = ({
  item,
  windowWidth,
  isSelected,
  isLoading,
  displayStatus,
  displayExcluded,
  hasZeroAmount = false,
  isLast = false,
  onAddToInventory,
  onRemoveItem,
  onSelectItem,
  onChangeAmount,
  onChangeExcluded,
}: IOrderItemProps) => {
  const [editing, setEditing] = useState(false);
  const [showImage, setShowImage] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  const checkboxChangeHandler = () => isSelected ? onRemoveItem() : onSelectItem();

  const imageUrls = useMemo((): string[] => {
    if (!item.images) {
      return [];
    }

    return item.images.map((image) => image.url);
  }, [item.images]);

  const toggleEdit = () => {
    setEditing(!editing);
  };

  const containerStyle = hasZeroAmount && displayStatus === OrderItemStatus.Active ? {
    backgroundColor: '#fff3cd',
    borderLeft: '4px solid #ffc107',
    ...(isLast && { borderRadius: '0 0 10px 10px' })
  } : {
    backgroundColor: '#fff',
    ...(isLast && { borderRadius: '0 0 10px 10px' })
  };

  // Log props to track state changes
  console.log(`🎭 OrderItem ${item.productNumber} render:`, {
    itemId: item.id,
    realStatus: item.status,
    displayStatus,
    realExcluded: item.excluded,
    displayExcluded,
    isLoading,
    taken: item.taken,
    amount: item.amount,
    hasZeroAmount,
    containerStyle
  });

  return (
    <div style={containerStyle}>
      <OrderItemContainer status={displayStatus}>
      {showImage && imageUrls && imageUrls.length > 0 && (
        <Lightbox
          mainSrc={imageUrls[photoIndex]}
          nextSrc={imageUrls[(photoIndex + 1) % imageUrls.length]}
          prevSrc={
            imageUrls[(photoIndex + imageUrls.length - 1) % imageUrls.length]
          }
          onCloseRequest={() => setShowImage(false)}
          onMovePrevRequest={() =>
            setPhotoIndex(
              (photoIndex + imageUrls.length - 1) % imageUrls.length
            )
          }
          onMoveNextRequest={() => setPhotoIndex((photoIndex + 1) % imageUrls.length)}
        />
      )}
      <Tooltip
        className="OrderItem__item-view-tooltip"
        hoverDelay={100}
        direction="right"
        content={
          item?.images?.[0] ? (
            <img
              style={{ maxHeight: "200px", maxWidth: "200px" }}
              src={getImageWithSize(item.images[0].url, "200x200")}
              alt="Fejl..."
            />
          ) : (
            <Icon name="noImageAvail" width={200} />
          )
          // <div className="Tooltip__content">
          //   {" "}
          //   {navigator.userAgent.match(/iPad/i) && (
          //     <div style={{ position: "absolute", top: "6px", right: "6px" }}>
          //       <ExitButton text="" solidBack={true} />
          //     </div>
          //   )}
          //   {getImageWithSize(item, "customData.billeder[0]", "200x200") ? (
          //     <ImageTooltip imageUrl={item?.images?.[0].url} />
          //   ) : (
          //     <Icon name="noImageAvail" width={200} />
          //   )}
          // </div>
        }
      >
        <div className="OrderItem__item-view">
          <Icon name="preview" width={20} />
          <div
            className="IntentoryListItem__hover-trigger"
            onClick={(e) => {
              e.preventDefault();
              setShowImage(true);
            }}
          />
        </div>
      </Tooltip>
      <div className="OrderItem__item-id ">
        {item.productNumber}
      </div>
      <div className="OrderItem__item-name ">{item.name}</div>
      <div className="OrderItem__item-description ">
        {item.description}
      </div>
      <div className="OrderItem__item-amount ">
        {editing ? (
          <div className="OrderItem__amount-editing">
            <div className="OrderItem__units-absolute">{""}</div>
            <input
              className="OrderItem__amount-input"
              value={item.amount}
              onChange={(e) => onChangeAmount(+e.target.value)}
            />
            <button className="OrderItem__amount-confirm" onClick={toggleEdit}>
              OK
            </button>
          </div>
        ) : (
          <div className="OrderItem__amount-readonly u-cursor-default">
            <div className="OrderItem__amount-text">
              {item.taken > 0 ? item.taken : item.amount}
            </div>
            <div className="OrderItem__amount-units">stk.</div>
            {displayStatus === OrderItemStatus.Active && (
              <div
                className="OrderItem__amount__icon u-cursor-pointer"
                onClick={toggleEdit}
              >
                <Icon name="PencilEdit" width={14} height={16} />
              </div>
            )}
          </div>
        )}
      </div>
      <div className="OrderItem__item-addToInv">
        {displayExcluded && displayStatus === OrderItemStatus.Completed ? (
          <div className="OrderItem__item-isAdded">
            <Icon name="addComplete" width={21} />
            <div className="OrderItem__item-isAdded__text">
              Undladt fra lager
            </div>
          </div>
        ) : displayStatus === OrderItemStatus.Completed ? (
          <div className="OrderItem__item-isAdded">
            <Icon name="addComplete" width={21} />
            <div className="OrderItem__item-isAdded__text">
              Tilføjet til lager
            </div>
          </div>
        ) : displayStatus === OrderItemStatus.Backorder ? (
          <div className="OrderItem__item-isAdded">
            <Icon name="orderTruck" width={21} />
            <div className="OrderItem__item-isAdded__text">Restordre</div>
          </div>
        ) : hasZeroAmount ? (
          <Tooltip
            content="Kan ikke tilføje vare med 0 antal til lager"
            direction="left"
            className="OrderItem__zero-amount-tooltip"
          >
            <div style={{ position: 'relative', height: '2.3em' }}>
              <SplitSelect
                defaultAddState={false}
                click={onAddToInventory}
                onChange={(addState) => onChangeExcluded(!addState)}
                disabled={isLoading}
                disableAddOnly={true}
              />
              {isLoading && (
                <div style={{ 
                  position: 'absolute', 
                  top: '50%', 
                  left: '50%', 
                  transform: 'translate(-50%, -50%)',
                  zIndex: 1
                }}>
                  <Icon name="buttonSpinner" width={16} pathStyle={{ fill: '#4092d7' }} />
                </div>
              )}
            </div>
          </Tooltip>
        ) : (
          <div style={{ position: 'relative', height: '2.3em' }}>
            <SplitSelect
              defaultAddState={!displayExcluded}
              click={onAddToInventory}
              onChange={(addState) => onChangeExcluded(!addState)}
              disabled={isLoading}
            />
            {isLoading && (
              <div style={{ 
                position: 'absolute', 
                top: '50%', 
                left: '50%', 
                transform: 'translate(-50%, -50%)',
                zIndex: 1
              }}>
                <Icon name="buttonSpinner" width={16} pathStyle={{ fill: '#4092d7' }} />
              </div>
            )}
          </div>
        )}
      </div>
      <div className="OrderItem__item-checkbox">
        {displayStatus !== OrderItemStatus.Active ? null : (
          <Checkbox checked={isSelected} change={checkboxChangeHandler} />
        )}
      </div>
      </OrderItemContainer>
    </div>
  );
};
